import { CLASSIC_SHOP_URL } from '../app/constants';
import { emit } from '../app/events';
import { PRODUCT_FIXTURES } from './product.fixtures';
import { ShopifyCartService } from './shopify.cart';
import { readShopifyConfig } from './shopify.client';
import { CommerceError, type CartLine, type CartLineInfo, type CartService } from './shopify.types';
import { readJson, writeJson } from '../utils/storage';

const CART_KEY = 'chienheureux:virtual-shop:cart:v1';

interface StoredCart {
  cartId: string;
  lines: CartLine[];
}

/** Repli quand l'appelant n'a pas fourni le détail : le catalogue local le connaît. */
function describeVariant(variantId: string): CartLineInfo {
  const fixture = PRODUCT_FIXTURES.find((product) => product.variantId === variantId);
  if (fixture) {
    return {
      title: fixture.title,
      price: fixture.price,
      imageUrl: fixture.imageUrl,
      imageAlt: fixture.imageAlt,
    };
  }
  return {
    title: 'Article',
    price: { amount: '0.00', currencyCode: 'EUR' },
    imageUrl: '',
    imageAlt: '',
  };
}

/** Un panier écrit par une version précédente n'a que variantId et quantity. */
function hydrate(stored: StoredCart | null): StoredCart | null {
  if (!stored?.cartId || !Array.isArray(stored.lines)) return null;
  const lines = stored.lines
    .filter((line): line is CartLine => Boolean(line?.variantId))
    .map((line) => {
      const info = describeVariant(line.variantId);
      return {
        variantId: line.variantId,
        quantity: line.quantity ?? 1,
        title: line.title ?? info.title,
        price: line.price ?? info.price,
        imageUrl: line.imageUrl ?? info.imageUrl,
        imageAlt: line.imageAlt ?? info.imageAlt,
      };
    });
  return { cartId: stored.cartId, lines };
}

/**
 * Panier simulé : même interface que le panier Storefront, mais tout reste local.
 * Aucun identifiant de variante n'est inventé, on ne travaille que sur ceux
 * fournis par le dépôt produits.
 */
export class MockCartService implements CartService {
  private cart: StoredCart | null = null;
  private counter = 0;

  constructor() {
    this.cart = hydrate(readJson<StoredCart>(CART_KEY));
  }

  async createCart(): Promise<string> {
    if (this.cart) return this.cart.cartId;
    this.counter += 1;
    this.cart = { cartId: `mock-cart-${this.counter}`, lines: [] };
    writeJson(CART_KEY, this.cart);
    return this.cart.cartId;
  }

  async addLine(variantId: string, quantity: number, info?: CartLineInfo): Promise<void> {
    if (!variantId) throw new CommerceError('Ce produit n’a pas de variante disponible.');
    if (quantity < 1) throw new CommerceError('Quantité invalide.');
    await this.createCart();
    const cart = this.cart as StoredCart;
    const existing = cart.lines.find((line) => line.variantId === variantId);
    if (existing) {
      existing.quantity += quantity;
    } else {
      cart.lines.push({ variantId, quantity, ...(info ?? describeVariant(variantId)) });
    }
    writeJson(CART_KEY, cart);
  }

  async updateLine(variantId: string, quantity: number): Promise<void> {
    if (quantity < 1) {
      await this.removeLine(variantId);
      return;
    }
    const cart = this.cart;
    const line = cart?.lines.find((item) => item.variantId === variantId);
    if (!cart || !line) throw new CommerceError('Cette ligne n’est plus dans le panier.');
    line.quantity = quantity;
    writeJson(CART_KEY, cart);
  }

  async removeLine(variantId: string): Promise<void> {
    const cart = this.cart;
    // Ligne déjà absente : le résultat souhaité est atteint, pas d'erreur.
    if (!cart) return;
    cart.lines = cart.lines.filter((line) => line.variantId !== variantId);
    writeJson(CART_KEY, cart);
  }

  async getLines(): Promise<CartLine[]> {
    // Copie défensive : l'affichage ne doit pas pouvoir modifier le panier.
    return (this.cart?.lines ?? []).map((line) => ({ ...line }));
  }

  async getCheckoutUrl(): Promise<string> {
    if (!this.cart || this.cart.lines.length === 0) {
      throw new CommerceError('Le panier est vide.');
    }
    // Checkout simulé : on renvoie vers le panier de la boutique classique.
    return `${CLASSIC_SHOP_URL}/cart?panier=${encodeURIComponent(this.cart.cartId)}`;
  }

  lineCount(): number {
    return this.cart?.lines.reduce((total, line) => total + line.quantity, 0) ?? 0;
  }
}

export type ShopCartService = CartService;

/** Total du panier, arrondi au centime, dans la devise des lignes. */
export function cartTotal(lines: CartLine[]): { amount: number; currencyCode: string } {
  let amount = 0;
  for (const line of lines) {
    const unit = Number.parseFloat(line.price.amount);
    if (!Number.isNaN(unit)) amount += unit * line.quantity;
  }
  return {
    amount: Math.round(amount * 100) / 100,
    currencyCode: lines[0]?.price.currencyCode ?? 'EUR',
  };
}

let service: ShopCartService | null = null;

/** Panier Storefront réel si Shopify est configuré, panier simulé sinon. */
export function getCartService(): ShopCartService {
  if (!service) {
    const config = readShopifyConfig();
    service = config ? new ShopifyCartService(config) : new MockCartService();
  }
  return service;
}

export function setCartService(next: ShopCartService): void {
  service = next;
}

/** Prévient l'interface (compteur de la barre) qu'une ligne a bougé. */
export function notifyCartChanged(): void {
  emit('cart:changed', { count: getCartService().lineCount() });
}
